import {
    HALF_WIDTH,
    FULL_WIDTH,
    FULL_HEIGHT,
    HALF_HEIGHT,
    EXTRA_HEIGHT,
} from '@/dashboards-v2/constants/widget-dimensions.constants';
import { DashboardWidgetType } from '@/types/dashboards/dashboard.types';
import { WidgetDomSettingsType } from '@/types/dashboards/widget.types';

// Exhaustive so a new DashboardWidgetType can't silently miss a config
// (e.g. dashboard lazy-load placeholder height — APP-160475).
const widgetDomSettingsMap: Record<DashboardWidgetType, WidgetDomSettingsType> = {
    activeVisitorsHourlyChart: {
        width: FULL_WIDTH,
    },
    aiAgentWidget: {
        width: FULL_WIDTH,
    },
    aiAgentQualityWidget: {
        width: FULL_WIDTH,
        showComparedToHeader: true,
    },
    aiAgentSuggestedPromptsChartWidget: {
        width: FULL_WIDTH,
    },
    aiAgentTopSuggestedPromptsWidget: {
        width: FULL_WIDTH,
    },
    appUsage: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    appVersion: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    captureOnSdkVersion: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    browserUsageChart: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    countableMetric: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        // Full-width tile row renders auto-height around a compact
        // stat-card strip, so reserve HALF_HEIGHT while lazy-loaded.
        placeholderHeight: HALF_HEIGHT,
        showGroupTags: true,
        deriveGroupsFromEvents: true,
        narrowEventTagsByProductArea: true,
    },
    countableChart: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
        narrowEventTagsByProductArea: true,
    },
    visitorAccountKeyMetrics: {
        width: FULL_WIDTH,
        autoHeight: true,
        placeholderHeight: HALF_HEIGHT,
    },
    productAreasOverviewMetrics: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        placeholderHeight: HALF_HEIGHT,
        showGroupTags: true,
        showAllProductAreasTag: true,
        hideNoProductArea: true,
    },
    productAreasOverviewChart: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
        showAllProductAreasTag: true,
        hideNoProductArea: true,
    },
    // Fixed height plus the widget-wrapper `min-height: 0` rule, matching appHealthTable.
    // Auto-height would ask the table to size itself against a card that has no page
    // scroll container, which leaves the body empty.
    productAreasList: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: true,
        showAllProductAreasTag: true,
        hideNoProductArea: true,
    },
    topVisitors: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: true,
    },
    topAccounts: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: true,
    },
    guidesTargeting: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: true,
        // The guides list is not segment-scoped (each row shows the guide's own
        // segment) — without this the tag row renders a misleading "Everyone".
        hideSegmentTag: true,
    },
    featuresTargeting: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: true,
    },
    objectAnalyticsKeyMetrics: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        placeholderHeight: HALF_HEIGHT,
        showGroupTags: true,
    },
    objectAnalyticsChart: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
    },
    webAnalyticsChart: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
    },
    webAnalyticsKeyMetrics: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
    },
    webAnalyticsBreakdownTable: {
        width: FULL_WIDTH,
        autoHeight: true,
        placeholderHeight: HALF_HEIGHT,
        showGroupTags: true,
    },
    appHealthTable: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
        contentFullWidth: true,
        showComparedToHeader: true,
    },
    csatScore: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    npsScore: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    npsByAccount: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    npsScoreDistribution: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    npsUsageBreakdown: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    surveyResponses: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    surveyResponsesTable: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    surveyTrends: {
        width: FULL_WIDTH,
        contentFullWidth: true,
        customCard: true,
    },
    customReportChart: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        requireTagInfo: true,
    },
    deviceUsage: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    dwmChart: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    featureAdoption: {
        width: FULL_WIDTH,
        showGroupTags: true,
    },
    feedbackHighlightsTopTopics: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showPendoAiTag: true,
    },
    feedbackSummary: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showPendoAiTag: true,
    },
    funnelReportChart: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        requireTagInfo: true,
    },
    retentionReportChart: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        requireTagInfo: true,
    },
    goalProgress: {
        width: FULL_WIDTH,
        tags: false,
    },
    guideActivity: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        hideSegmentTag: false,
    },
    guideExperimentSummary: {
        width: FULL_WIDTH,
        customCard: true,
        tags: false,
        contentFullWidth: true,
    },
    guideEffectivenessMetrics: {
        width: FULL_WIDTH,
        autoHeight: true,
        showGroupTags: true,
        showAllProductAreasTag: true,
    },
    guideEffectivenessPerformance: {
        width: FULL_WIDTH,
        autoHeight: true,
        showGroupTags: true,
        showAllProductAreasTag: true,
    },
    guideOverview: {
        width: HALF_WIDTH,
        height: HALF_HEIGHT,
        hideSegmentTag: true,
        showGroupTags: true,
        showAllProductAreasTag: true,
    },
    guideTopMetrics: {
        width: FULL_WIDTH,
        height: HALF_HEIGHT,
        hideSegmentTag: false,
    },
    guideElements: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    timeOnGuide: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        hideSegmentTag: false,
    },
    guideEngagement: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
        showAllProductAreasTag: true,
    },
    guideGoal: {
        width: FULL_WIDTH,
        height: HALF_HEIGHT,
        contentFullWidth: true,
        hideSegmentTag: true,
    },
    htmlEmbed: {
        width: FULL_WIDTH,
        tags: false,
    },
    professionalServicesCustomWidget: {
        width: FULL_WIDTH,
    },
    metadataDetails: {
        width: FULL_WIDTH,
        contentFullWidth: true,
    },
    mostSeenGuides: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: true,
        showAllProductAreasTag: true,
    },
    npsWidget: {
        contentFullWidth: true,
    },
    npsScatterPlot: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    osChart: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    osVersion: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    pageFeatureUseOverTime: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
        deriveGroupsFromEvents: true,
        narrowEventTagsByProductArea: true,
    },
    pathReportChart: {
        width: FULL_WIDTH,
        height: EXTRA_HEIGHT,
        requireTagInfo: true,
    },
    perWeekChart: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    pesTrending: {
        width: FULL_WIDTH,
    },
    pmfScore: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    pollResults: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        hideChannelTag: true,
    },
    prioritizeIdeas: {
        width: FULL_WIDTH,
        showGroupTags: true,
        showStatusTags: true,
        showCreatedByTags: true,
        showImportanceTags: true,
        showLabelsTags: true,
        showSearchTextTags: true,
    },
    productEngagementScore: {
        width: FULL_WIDTH,
        customCard: true,
    },
    productRequestsByGroup: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        hideSegmentTag: true,
    },
    productRequestsByStatus: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        hideSegmentTag: true,
    },
    recentProductRequests: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        hideSegmentTag: true,
        contentFullWidth: true,
    },
    replaySavedFilter: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        requireTagInfo: true,
    },
    retentionByUsage: {
        width: FULL_WIDTH,
    },
    richTextWidget: {
        width: FULL_WIDTH,
        contentFullWidth: true,
        customCard: true,
    },
    savedViewsCountOverTime: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: false,
        showStatusTags: false,
        showCreatedByTags: false,
        showImportanceTags: false,
        showLabelsTags: false,
        showSearchTextTags: false,
        showLinkedIdeasTags: false,
    },
    savedViewsProductArea: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: false,
        showStatusTags: false,
        showCreatedByTags: false,
        showImportanceTags: false,
        showLabelsTags: false,
        showSearchTextTags: false,
        showLinkedIdeasTags: false,
    },
    savedViewsStatus: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: false,
        showStatusTags: false,
        showCreatedByTags: false,
        showImportanceTags: false,
        showLabelsTags: false,
        showSearchTextTags: false,
        showLinkedIdeasTags: false,
    },
    savedViewsSummary: {
        width: FULL_WIDTH,
        contentFullWidth: true,
        customCard: true,
    },
    savedViewsTopAccounts: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        showGroupTags: false,
        showStatusTags: false,
        showCreatedByTags: false,
        showImportanceTags: false,
        showLabelsTags: false,
        showSearchTextTags: false,
        showLinkedIdeasTags: false,
    },
    scatterChart: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
    },
    timeToNth: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        // widget-wrapper's widgetHeight bumps full-width time-to-nth to 720px
        // (see the timeToNth branch there); mirror that so the placeholder
        // doesn't jump on reveal.
        placeholderHeight: '720px',
        showGroupTags: true,
    },
    stickinessMetric: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
    surveyMetrics: {
        width: HALF_WIDTH,
        autoHeight: true,
    },
    surveySummary: {
        width: FULL_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
        customCard: true,
    },
    tagOverview: {
        width: HALF_WIDTH,
        height: HALF_HEIGHT,
        hideSegmentTag: true,
        showGroupTags: true,
        showAllProductAreasTag: true,
    },
    timeToFirstUse: {
        width: FULL_WIDTH,
        contentFullWidth: true,
    },
    umuxScore: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        contentFullWidth: true,
    },
    visitorOverview: {
        width: HALF_WIDTH,
        height: HALF_HEIGHT,
        showGroupTags: true,
    },
    objectOverview: {
        width: HALF_WIDTH,
        height: HALF_HEIGHT,
        showGroupTags: true,
    },
    licenseUtilization: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
        showGroupTags: true,
    },
    voteCount: {
        width: FULL_WIDTH,
        showGroupTags: true,
        showStatusTags: true,
        showCreatedByTags: true,
        showImportanceTags: true,
        showLabelsTags: true,
        showSearchTextTags: true,
    },
    workflowWidget: {
        width: HALF_WIDTH,
        height: FULL_HEIGHT,
    },
};

export default widgetDomSettingsMap;
